import { router } from "@inertiajs/react";
import { useState } from "react";
import AppShell from "../../../components/Shell/AppShell";
import { formatTokensPl } from "../../../lib/tokens";
import { getLanguage, pick } from "../../../lib/i18n";
import { CurrentUser } from "../../../types";

const copy = {
  pl: { emptyRange: "Brak zarejestrowanego zużycia tokenów dla wybranego zakresu.", operation: "Operacja", tokens: "Tokeny", share: "Udział", events: "Zdarzenia", user: "Użytkownik", date: "Data", prompt: "Prompt", output: "Output", title: "Zużycie tokenów", subtitle: "Raport administracyjny wg operacji, użytkowników i dat.", from: "Od", to: "Do", filter: "Filtruj", clear: "Wyczyść", totalLabel: "Łącznie", tokensWord: "tokenów", eventsLabel: "Zdarzenia", byOperation: "Wg operacji", byUser: "Wg użytkowników", byDate: "Wg daty", byModel: "Wg modelu" },
  en: { emptyRange: "No recorded token usage for the selected range.", operation: "Operation", tokens: "Tokens", share: "Share", events: "Events", user: "User", date: "Date", prompt: "Prompt", output: "Output", title: "Token usage", subtitle: "Admin report by operation, users and dates.", from: "From", to: "To", filter: "Filter", clear: "Clear", totalLabel: "Total", tokensWord: "tokens", eventsLabel: "Events", byOperation: "By operation", byUser: "By user", byDate: "By date", byModel: "By model" },
};
const tr = () => pick(copy);

type Aggregate = {
  events_count: number;
  prompt_tokens: number;
  output_tokens: number;
  total_tokens: number;
};

type OperationRow = Aggregate & {
  operation: string;
  label: string;
};

type UserRow = Aggregate & {
  user_id: number | null;
  email: string | null;
  name: string;
};

type DateRow = Aggregate & {
  date: string;
};

type ModelRow = Aggregate & {
  model: string;
};

type TokenUsageReport = {
  summary: Aggregate;
  by_operation: OperationRow[];
  by_user: UserRow[];
  by_date: DateRow[];
  by_model: ModelRow[];
};

type Props = {
  filters: {
    date_from: string | null;
    date_to: string | null;
  };
  report: TokenUsageReport;
  current_user: CurrentUser;
  products?: { id: number; name: string }[];
};

function initialsFrom(name: string): string {
  return name
    .split(" ")
    .map((word) => word.at(0) ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function formatDate(date: string): string {
  return new Intl.DateTimeFormat(getLanguage() === "en" ? "en-GB" : "pl-PL", { year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(date));
}

function percentOf(value: number, total: number): string {
  if (total <= 0) return "0%";
  return `${Math.round((value / total) * 100)}%`;
}

function SummaryMetric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="mono" style={{ fontSize: 10, color: "var(--muted-2)", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 650, color: "var(--ink)", marginTop: 3 }}>{value}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{ fontSize: 13, color: "var(--muted)", padding: "12px 0" }}>
      {tr().emptyRange}
    </div>
  );
}

function OperationTable({ rows, totalTokens }: { rows: OperationRow[]; totalTokens: number }) {
  if (rows.length === 0) return <EmptyState />;

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr style={{ color: "var(--muted)", textAlign: "left", borderBottom: "1px solid var(--line)" }}>
          <th style={{ padding: "0 0 8px", fontWeight: 600 }}>{tr().operation}</th>
          <th style={{ padding: "0 0 8px", fontWeight: 600, textAlign: "right" }}>{tr().tokens}</th>
          <th style={{ padding: "0 0 8px", fontWeight: 600, textAlign: "right" }}>{tr().share}</th>
          <th style={{ padding: "0 0 8px", fontWeight: 600, textAlign: "right" }}>{tr().events}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.operation} style={{ borderBottom: "1px solid var(--line)" }}>
            <td style={{ padding: "9px 0", color: "var(--ink)" }}>{row.label}</td>
            <td style={{ padding: "9px 0", textAlign: "right", color: "var(--ink)", fontWeight: 600 }}>{formatTokensPl(row.total_tokens)}</td>
            <td style={{ padding: "9px 0", textAlign: "right", color: "var(--muted)" }}>{percentOf(row.total_tokens, totalTokens)}</td>
            <td style={{ padding: "9px 0", textAlign: "right", color: "var(--muted)" }}>{row.events_count}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function UserTable({ rows, totalTokens }: { rows: UserRow[]; totalTokens: number }) {
  if (rows.length === 0) return <EmptyState />;

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr style={{ color: "var(--muted)", textAlign: "left", borderBottom: "1px solid var(--line)" }}>
          <th style={{ padding: "0 0 8px", fontWeight: 600 }}>{tr().user}</th>
          <th style={{ padding: "0 0 8px", fontWeight: 600, textAlign: "right" }}>{tr().tokens}</th>
          <th style={{ padding: "0 0 8px", fontWeight: 600, textAlign: "right" }}>{tr().share}</th>
          <th style={{ padding: "0 0 8px", fontWeight: 600, textAlign: "right" }}>{tr().events}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.user_id ?? "unknown"} style={{ borderBottom: "1px solid var(--line)" }}>
            <td style={{ padding: "9px 0", color: "var(--ink)" }}>
              <div style={{ fontWeight: 600 }}>{row.name}</div>
              {row.email && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{row.email}</div>}
            </td>
            <td style={{ padding: "9px 0", textAlign: "right", color: "var(--ink)", fontWeight: 600 }}>{formatTokensPl(row.total_tokens)}</td>
            <td style={{ padding: "9px 0", textAlign: "right", color: "var(--muted)" }}>{percentOf(row.total_tokens, totalTokens)}</td>
            <td style={{ padding: "9px 0", textAlign: "right", color: "var(--muted)" }}>{row.events_count}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DateTable({ rows }: { rows: DateRow[] }) {
  if (rows.length === 0) return <EmptyState />;

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr style={{ color: "var(--muted)", textAlign: "left", borderBottom: "1px solid var(--line)" }}>
          <th style={{ padding: "0 0 8px", fontWeight: 600 }}>{tr().date}</th>
          <th style={{ padding: "0 0 8px", fontWeight: 600, textAlign: "right" }}>{tr().tokens}</th>
          <th style={{ padding: "0 0 8px", fontWeight: 600, textAlign: "right" }}>{tr().prompt}</th>
          <th style={{ padding: "0 0 8px", fontWeight: 600, textAlign: "right" }}>{tr().output}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.date} style={{ borderBottom: "1px solid var(--line)" }}>
            <td style={{ padding: "9px 0", color: "var(--ink)" }}>{formatDate(row.date)}</td>
            <td style={{ padding: "9px 0", textAlign: "right", color: "var(--ink)", fontWeight: 600 }}>{formatTokensPl(row.total_tokens)}</td>
            <td style={{ padding: "9px 0", textAlign: "right", color: "var(--muted)" }}>{formatTokensPl(row.prompt_tokens)}</td>
            <td style={{ padding: "9px 0", textAlign: "right", color: "var(--muted)" }}>{formatTokensPl(row.output_tokens)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ModelTable({ rows }: { rows: ModelRow[] }) {
  if (rows.length === 0) return <EmptyState />;

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <tbody>
        {rows.map((row) => (
          <tr key={row.model} style={{ borderBottom: "1px solid var(--line)" }}>
            <td style={{ padding: "8px 0", color: "var(--ink)" }}>{row.model}</td>
            <td style={{ padding: "8px 0", textAlign: "right", color: "var(--ink)", fontWeight: 600 }}>{formatTokensPl(row.total_tokens)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function AdminTokenUsageReport({ filters, report, current_user, products = [] }: Props) {
  const [dateFrom, setDateFrom] = useState(filters.date_from ?? "");
  const [dateTo, setDateTo] = useState(filters.date_to ?? "");
  const initials = initialsFrom(current_user.name);

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    router.get("/admin/reports/token_usage", {
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }, { preserveState: true });
  }

  return (
    <AppShell products={products} user={{ name: current_user.name, initials, role: current_user.role }}>
      <div className="page" style={{ maxWidth: 920 }}>
        <div className="page-hd">
          <div>
            <h2>{tr().title}</h2>
            <p>{tr().subtitle}</p>
          </div>
        </div>

        <form onSubmit={submit} className="panel" style={{ marginBottom: 14 }}>
          <div className="panel-body">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, alignItems: "end" }}>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="token-usage-date-from">{tr().from}</label>
                <input
                  id="token-usage-date-from"
                  className="input"
                  type="date"
                  value={dateFrom}
                  onChange={(event) => setDateFrom(event.target.value)}
                />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="token-usage-date-to">{tr().to}</label>
                <input
                  id="token-usage-date-to"
                  className="input"
                  type="date"
                  value={dateTo}
                  onChange={(event) => setDateTo(event.target.value)}
                />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="submit" className="btn btn-primary btn-sm">{tr().filter}</button>
                <a href="/admin/reports/token_usage" className="btn btn-ghost btn-sm">{tr().clear}</a>
              </div>
            </div>
          </div>
        </form>

        <div className="panel" style={{ marginBottom: 14 }}>
          <div className="panel-body">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 18 }}>
              <SummaryMetric label={tr().totalLabel} value={`${formatTokensPl(report.summary.total_tokens)} ${tr().tokensWord}`} />
              <SummaryMetric label={tr().prompt} value={formatTokensPl(report.summary.prompt_tokens)} />
              <SummaryMetric label={tr().output} value={formatTokensPl(report.summary.output_tokens)} />
              <SummaryMetric label={tr().eventsLabel} value={String(report.summary.events_count)} />
            </div>
          </div>
        </div>

        <div className="panel" style={{ marginBottom: 14 }}>
          <div className="panel-hd"><h3>{tr().byOperation}</h3></div>
          <div className="panel-body">
            <OperationTable rows={report.by_operation} totalTokens={report.summary.total_tokens} />
          </div>
        </div>

        <div className="panel" style={{ marginBottom: 14 }}>
          <div className="panel-hd"><h3>{tr().byUser}</h3></div>
          <div className="panel-body">
            <UserTable rows={report.by_user} totalTokens={report.summary.total_tokens} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
          <div className="panel">
            <div className="panel-hd"><h3>{tr().byDate}</h3></div>
            <div className="panel-body">
              <DateTable rows={report.by_date} />
            </div>
          </div>

          <div className="panel">
            <div className="panel-hd"><h3>{tr().byModel}</h3></div>
            <div className="panel-body">
              <ModelTable rows={report.by_model} />
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
